import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@store/auth.store';
import { can } from '@/shared/hooks/usePermissions';
import { useProfile } from '@/shared/hooks/useAuth';
import type { UserRole } from '@/shared/types';

interface ProtectedRouteProps {
  /** Legacy role gate — prefer `permission`; kept for routes not yet mapped. */
  allowedRoles?: UserRole[];
  /**
   * RBAC permission key required to enter (matrix on /settings/roles).
   * ADMIN always passes; stale sessions without a permissions array pass too
   * (the backend 403 is the real gate — see shared/hooks/usePermissions.ts).
   */
  permission?: string;
  redirectTo?: string;
}

export function ProtectedRoute({ allowedRoles, permission, redirectTo = '/login' }: ProtectedRouteProps) {
  const { isAuthenticated, user, isHydrated } = useAuthStore();
  // B-519: the session in localStorage carries the permissions from the LAST profile sync, so a key
  // added by a release (e.g. `fleet.map.view`) is missing there until useProfileSync (AppLayout)
  // refreshes it. Same query key as that sync — no extra request.
  const profile = useProfile();

  // Wait for store to rehydrate from localStorage
  if (!isHydrated) return null;

  if (!isAuthenticated) return <Navigate to={redirectTo} replace />;

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/403" replace />;
  }

  if (permission) {
    // The fresh profile wins over the stored session as soon as it arrives — deciding on the stored
    // one in that render would still bounce to /403 before the sync effect writes it to the store.
    // Only the SAME user's profile. Since B-523 the query cache is cleared on every account change
    // (shared/lib/queryClient.ts); this id check stays as a cheap second guard, not the only one.
    const fresh = profile.data && user && profile.data.id === user.id ? profile.data : null;
    const subject = fresh ? { ...user, ...fresh } : user;
    if (!can(subject, permission)) {
      // First sync of this page load still running → wait instead of sending a reload of a page the
      // user may open (e.g. right after a release) to /403. A failed sync falls through to /403.
      if (profile.isLoading) return null;
      return <Navigate to="/403" replace />;
    }
  }

  return <Outlet />;
}

/** Blokuje dostęp do wszystkich stron gdy użytkownik musi zmienić hasło */
export function MustChangePasswordGuard() {
  const { isAuthenticated, user, isHydrated } = useAuthStore();

  if (!isHydrated) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (user?.mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  return <Outlet />;
}

export function PublicOnlyRoute() {
  const { isAuthenticated, user, isHydrated } = useAuthStore();

  if (!isHydrated) return null;
  if (isAuthenticated) {
    if (user?.mustChangePassword) return <Navigate to="/change-password" replace />;
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
